import React, { ChangeEvent, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import GTable from 'components/tables/GTable';
import { Alert, Row, Col, Button } from 'react-bootstrap';
import { columns } from './columns/columns';
import { GET_ALL_ROLES } from 'services/api/role';
import PageBreadcrumb from 'components/common/PageBreadcrumb';
import { RoleBreadcrumbItems } from 'data/commonData';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import SearchBox from 'components/common/SearchBox';
import { RoleResponse } from './types/types';
import RoleModal from './RoleModal/RoleModal';
import DeleteRoleModal from './RoleModal/DeleteRoleModal';
import LoadingPlaceholder from 'components/loading-placeholder/LoadingPlaceholder';
import RoleDetailViewModal from './RoleModal/RoleDetailViewModal';
import { usePermissions } from 'hooks/usePermissions';
import { PERMISSIONS } from 'constants/permissions';

const Roles = () => {
  const queryClient = useQueryClient();
  const [showRoleModal, setShowRoleModal] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [selectedRole, setSelectedRole] = useState<RoleResponse | undefined>(
    undefined
  );
  const [showPermissionsModal, setShowPermissionsModal] =
    useState<boolean>(false);
  const [roleToDelete, setRoleToDelete] = useState<RoleResponse | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [permissionsLoading, setPermissionsLoading] = useState<boolean>(false);

  const [canCreate, canEdit, canDelete] = usePermissions([
    PERMISSIONS.ROLES.CREATE,
    PERMISSIONS.ROLES.UPDATE,
    PERMISSIONS.ROLES.DELETE
  ]);

  const {
    data: rolesResponse,
    isLoading,
    isError,
    error
  } = useQuery({
    queryKey: ['roles'],
    queryFn: GET_ALL_ROLES,
    staleTime: 3 * 60 * 60 * 1000,
    select: response => response.data
  });

  const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const filterRoles = (roles: RoleResponse[], term: string): RoleResponse[] => {
    if (!term) return roles;
    const searchLower = term.toLowerCase();
    return roles.filter(role => role.name.toLowerCase().includes(searchLower));
  };

  const handleAddNewRole = () => {
    setSelectedRole(undefined);
    setShowRoleModal(true);
    setPermissionsLoading(true);
  };

  const handleEditClick = (role: RoleResponse) => {
    setSelectedRole(role);
    setShowRoleModal(true);
    setPermissionsLoading(true);
  };
  const handleDeleteClick = (role: RoleResponse) => {
    setRoleToDelete(role);
    setShowDeleteModal(true);
  };

  const handleViewPermissions = (role: RoleResponse) => {
    setSelectedRole(role);
    setShowPermissionsModal(true);
  };

  const handleSuccess = () => {
    queryClient.invalidateQueries(['roles']);
  };

  const handleModalHide = () => {
    setShowRoleModal(false);
    setPermissionsLoading(false);
  };

  const rolesColumns = useMemo(
    () =>
      columns(
        handleEditClick,
        canEdit,
        handleDeleteClick,
        canDelete,
        handleViewPermissions
      ),
    [canEdit, canDelete]
  );

  if (isLoading) return <LoadingPlaceholder />;
  if (isError) {
    return (
      <Alert variant="danger">
        Failed to fetch roles:{' '}
        {(error as any).response.data.message || 'Something went wrong'}
      </Alert>
    );
  }

  const filteredRoles = filterRoles(
    rolesResponse?.sort((a: any, b: any) => {
      return a?.name?.localeCompare(b?.name ?? '');
    }) ?? [],
    searchTerm
  );

  return (
    <div>
      <PageBreadcrumb items={RoleBreadcrumbItems} />
      <Row className="g-3 mb-4">
        <Col xs={12}>
          <h2 className="mb-1">
            <span className="me-3">Roles</span>
            <span className="fw-normal text-700">
              ({filteredRoles?.length})
            </span>
          </h2>
        </Col>
        <Col
          xs={12}
          className="d-flex justify-content-between align-items-center"
        >
          <SearchBox
            placeholder="Search by role name"
            onChange={handleSearchInputChange}
            value={searchTerm}
            className="w-40"
          />
          {canCreate && (
            <Button
              variant="primary"
              className="d-flex align-items-center gap-2"
              onClick={handleAddNewRole}
            >
              <FontAwesomeIcon icon={faPlus} />
              Add new role
            </Button>
          )}
        </Col>
      </Row>
      <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 bg-white border-top border-bottom border-200 position-relative top-1 ">
        {isLoading ? (
          <LoadingPlaceholder />
        ) : (
          <GTable
            data={filteredRoles || []}
            columns={rolesColumns}
            isLoading={isLoading}
          />
        )}
      </div>
      <RoleModal
        show={showRoleModal}
        onHide={handleModalHide}
        role={selectedRole}
        onSuccess={handleSuccess}
        permissionsLoading={permissionsLoading}
        setPermissionsLoading={setPermissionsLoading}
      />
      <DeleteRoleModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        role={roleToDelete}
        onSuccess={handleSuccess}
      />
      <RoleDetailViewModal
        show={showPermissionsModal}
        onHide={() => setShowPermissionsModal(false)}
        role={selectedRole}
      />
    </div>
  );
};

export default Roles;
